import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ShieldCheck, ShieldX, ShieldAlert, Upload,
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, FileCode2,
} from 'lucide-react';
import { licenseApi } from '../services/licenseApi';
import { useLicense } from '../context/LicenseContext';

// ---------------------------------------------------------------------------
// Badge statut
// ---------------------------------------------------------------------------
const StatusBadge = ({ valid, expired, loaded }) => {
  if (!loaded)  return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500"><ShieldAlert className="h-4 w-4" /> Aucune licence</span>;
  if (valid)    return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"><ShieldCheck className="h-4 w-4" /> Valide</span>;
  if (expired)  return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800"><ShieldAlert className="h-4 w-4" /> Expirée</span>;
  return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700"><ShieldX className="h-4 w-4" /> Invalide</span>;
};

// ---------------------------------------------------------------------------
// Page principale
// ---------------------------------------------------------------------------
const LicensePage = () => {
  const { refresh: refreshGlobalLicense } = useLicense();

  const [status, setStatus]           = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver]       = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchStatus = useCallback(() => {
    setLoadingStatus(true);
    licenseApi.getStatus()
      .then(res => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoadingStatus(false));
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const processFile = useCallback(async (file) => {
    if (!file || !file.name.endsWith('.xml')) {
      setUploadResult({ success: false, error: 'Le fichier doit être au format .xml' });
      return;
    }
    setPendingFile(file);
    setUploading(true);
    setUploadResult(null);
    try {
      const res = await licenseApi.upload(file);
      setUploadResult({ success: true, data: res.data });
      fetchStatus();
      refreshGlobalLicense();
    } catch (err) {
      setUploadResult({
        success: false,
        error: err.response?.data?.detail || 'Erreur lors du chargement du fichier',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [fetchStatus, refreshGlobalLicense]);

  const handleFileChange = (e) => processFile(e.target.files?.[0]);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true);  };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop      = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

      {/* Header */}
      <div className="border-b border-slate-200 pb-4">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#004A99]" />
          Gestion des licences
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Activez les modules de l'application en chargeant le fichier XML fourni par votre prestataire.
        </p>
      </div>

      {/* Statut courant */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Licence active</h2>
          <button
            onClick={fetchStatus}
            disabled={loadingStatus}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loadingStatus ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        <div className="px-5 py-4">
          {loadingStatus ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <RefreshCw className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : status ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <StatusBadge valid={status.valid} expired={status.expired} loaded={status.loaded} />
                {status.error && !status.valid && (
                  <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">{status.error}</span>
                )}
              </div>

              {status.loaded ? (
                <div className="grid grid-cols-3 gap-4 pt-1">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">Client</p>
                    <p className="text-sm text-slate-800 font-medium">{status.client || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">Expiration</p>
                    <p className="text-sm text-slate-800 font-medium">{status.expiry || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">Modules</p>
                    <div className="flex flex-wrap gap-1">
                      {(status.modules || []).map(m => (
                        <span key={m} className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-800 text-xs rounded font-mono">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  Aucune licence chargée. L'application fonctionne en mode permissif.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-red-600">Impossible de récupérer le statut de la licence.</p>
          )}
        </div>
      </div>

      {/* Zone upload */}
      <div className="bg-white border border-slate-200 shadow-sm">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Charger une licence</h2>
          </div>

          <div className="px-5 py-5 space-y-4">
            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`relative flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed rounded cursor-pointer transition-all select-none ${
                uploading
                  ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
                  : dragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,application/xml,text/xml"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
              />

              {uploading ? (
                <>
                  <RefreshCw className="h-8 w-8 text-blue-400 animate-spin" />
                  <p className="text-sm text-slate-500">Chargement de <span className="font-medium text-slate-700">{pendingFile?.name}</span>…</p>
                </>
              ) : dragOver ? (
                <>
                  <Upload className="h-8 w-8 text-blue-500" />
                  <p className="text-sm font-medium text-blue-700">Relâchez pour charger</p>
                </>
              ) : (
                <>
                  <div className="p-3 bg-slate-100 rounded-full">
                    <FileCode2 className="h-7 w-7 text-slate-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">
                      Glissez-déposez le fichier <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">.xml</code> ici
                    </p>
                    <p className="text-xs text-slate-400 mt-1">ou cliquez pour sélectionner</p>
                  </div>
                </>
              )}
            </div>

            {/* Résultat upload */}
            {uploadResult && (
              <div className={`flex items-start gap-3 p-4 rounded border text-sm ${
                uploadResult.success && uploadResult.data?.valid
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : uploadResult.success
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {uploadResult.success && uploadResult.data?.valid
                  ? <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
                  : uploadResult.success
                  ? <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                  : <XCircle className="h-5 w-5 mt-0.5 shrink-0" />
                }
                <div className="space-y-0.5">
                  {uploadResult.success
                    ? uploadResult.data?.valid
                      ? <>
                          <p className="font-medium">Licence activée avec succès</p>
                          <p className="text-xs opacity-80">Client : {uploadResult.data.client} · Expire le {uploadResult.data.expiry}</p>
                        </>
                      : <>
                          <p className="font-medium">Fichier chargé mais licence non valide</p>
                          <p className="text-xs opacity-80">{uploadResult.data?.error}</p>
                        </>
                    : <>
                        <p className="font-medium">Échec du chargement</p>
                        <p className="text-xs opacity-80">{uploadResult.error}</p>
                      </>
                  }
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400">
              Seuls les fichiers <code className="font-mono">.xml</code> signés par le prestataire sont acceptés.
              La licence est vérifiée cryptographiquement à l'activation.
            </p>
          </div>
      </div>

    </div>
  );
};

export default LicensePage;
